/* eslint-disable max-lines */
import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  SimpleChanges,
  ViewChild,
  input,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import type { ShareOnTheGoDialogResult, ShareOnTheGoI18n } from '@core/access-to-candidate-legacy';
import { SHARE_ON_THE_GO_I18N, ShareOnTheGoService } from '@core/access-to-candidate-legacy';
import type { User } from '@core/account-types';
import { AddEvaluationDropdownComponent } from '@core/add-evaluations-dropdown-legacy';
import type { QuickEvaluationContext } from '@core/analytics-legacy';
import type { FullCandidate, ShareOnTheGoRequestPayload } from '@core/candidate-types';
import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { InterviewResultRequestActions } from '@core/interview-result-request-data';
import { InterviewResultsActions } from '@core/interview-results-data';
import type { InterviewResult } from '@core/interview-results-types';
import { InterviewTemplateScope } from '@core/interview-templates-data';
import { AppNavigatorService } from '@core/navigation-legacy';
import type { PlacementOfferSelectOption } from '@core/offers-ui';
import { OfferSelectOptionHelper } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import { EvaluationsAnalytics, WorkflowTemplatesAnalytics } from '@core/temp-legacy';
import type { SelectGroupedOptions } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  GroupComponent,
  IconComponent,
  OverlayOriginDirective,
  RtStatefulComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';
import { WorkflowTemplatesFeatures } from '@core/workflow-templates-types';

import { AddQuickEvaluationDropdownComponent } from '../add-quick-evaluation-dropdown/add-quick-evaluation-dropdown.component';
import { InterviewKind } from '../interview-kind';
import { InterviewTemplateSelectService } from '../interview-template-select.service';

type State = {
  templates: SelectGroupedOptions[];
  offers: PlacementOfferSelectOption[];
  hover: boolean;
  myLastRating: string | null;
};

@Component({
  selector: 'rt-add-evaluation',
  templateUrl: 'add-evaluation.component.html',
  styleUrls: ['./add-evaluation.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    WorkflowTemplatesAnalytics,
    EvaluationsAnalytics,
    {
      provide: SHARE_ON_THE_GO_I18N,
      useValue: <ShareOnTheGoI18n>{
        submitBtn: 'app.candidate.share_on_the_go.allow_access_to_candidate.request_evaluation',
        dontRequestOption:
          'app.candidate.share_on_the_go.allow_access_to_candidate.dont_request_evaluation_from',
        dontRequestOptionTooltip:
          'app.candidate.share_on_the_go.allow_access_to_candidate.skip_user_tooltip',
      },
    },
  ],
  imports: [
    OverlayOriginDirective,
    GroupComponent,
    ButtonComponent,
    IconComponent,
    ConnectedOverlayDirective,
    AddEvaluationDropdownComponent,
    AddQuickEvaluationDropdownComponent,
    TranslatePipe,
  ],
})
export class AddEvaluationComponent extends RtStatefulComponent<State> {
  private readonly appNavigator = inject(AppNavigatorService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly analyticsWorkflowTemplates = inject(WorkflowTemplatesAnalytics);
  private readonly evaluationsAnalytics = inject(EvaluationsAnalytics);
  private readonly shareOnTheGoService = inject(ShareOnTheGoService);

  private readonly interviewTemplateService = inject(InterviewTemplateSelectService);
  protected readonly cdRef: ChangeDetectorRef;
  public readonly quickDropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  public readonly candidate = input<FullCandidate>(undefined!);
  public readonly placement = input<Placement>();
  public readonly hasBorder = input<boolean>(undefined!);
  public readonly disabled = input<boolean>(undefined!);
  public readonly allowedUsers = input<User[]>(undefined!);
  public readonly isActionToolbar = input<boolean>(false);
  public readonly evalDropdownOrigin = input<OverlayOriginDirective>(undefined!);
  public readonly hasTriggerContent = input<boolean>(true);
  public readonly currentUserAvailablePlacements = input<Placement[]>([]);
  public readonly hasAccessToAllJobOffers = input<boolean>(false);
  public readonly quickEvaluationTrigger = input<QuickEvaluationContext>(undefined!);
  @Input() public hasQuickEvaluationDropdown: boolean = false;

  public optionsClick = output<void>();
  public evalDropdownOpen = output<void>();
  public evalDropdownClose = output<void>();

  @ViewChild('evalDropdown', { static: false })
  public evalDropdownTpl: ConnectedOverlayDirective;
  @ViewChild('defaultEvalDropdownTrigger', { static: false })
  public defaultEvalDropdownTrigger: OverlayOriginDirective;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      templates: [],
      offers: [],
      hover: false,
      myLastRating: null,
    });
  }

  public ngOnInit(): void {
    this.getData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.candidate || changes.currentUserAvailablePlacements) {
      this.setState({
        ...this.state,
        offers: OfferSelectOptionHelper.placementsToOfferSelectOptions(
          this.currentUserAvailablePlacements(),
        ),
        myLastRating: this.candidate()?.myLastRating,
      });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public handleClick(): void {
    if (this.hasQuickEvaluationDropdown) {
      this.openDropdown();
    } else {
      this.optionsClick.emit();
    }
  }

  public hasMyLastRating(): boolean {
    return this.state.myLastRating !== null;
  }

  public onMouseEnter(): void {
    this.setState({
      ...this.state,
      hover: true,
    });
  }

  public onMouseLeave(): void {
    this.setState({
      ...this.state,
      hover: false,
    });
  }

  public hasRating(candidate: FullCandidate): boolean {
    return candidate.positiveRatings !== null || candidate.ratingVisible === false;
  }

  public onSubmit({
    event,
  }: {
    event: {
      type: string;
      value: { talentPoolId?: number; offerId?: number; templateId: number; admins: User[] };
    };
  }): void {
    switch (event.type) {
      case 'addEvaluation': {
        this.analyticsWorkflowTemplates.trackTemplateInsertion(
          WorkflowTemplatesFeatures.EvaluationForms,
        );
        this.createInterviewResult(event.value);
        break;
      }
      case 'addInterview': {
        this.analyticsWorkflowTemplates.trackTemplateInsertion(
          WorkflowTemplatesFeatures.EvaluationForms,
        );
        this.evaluationsAnalytics.trackRequestEvaluation();

        this.appNavigator.openCandidateDialog(this.candidate().id, {
          openInterview: true,
          interviewOfferId: event.value.offerId,
          interviewTalentPoolId: event.value.talentPoolId,
          interviewTemplateId: event.value.templateId,
        });
        break;
      }
      case 'addInterviewRequest': {
        this.evaluationsAnalytics.trackRequestEvaluation();
        this.onAddInterviewRequest({ ...event.value });
        break;
      }
      default:
        return;
    }
  }

  public openDropdown(evalDropdown: ConnectedOverlayDirective = this.evalDropdownTpl): void {
    evalDropdown.origin = this.evalDropdownOrigin() || this.defaultEvalDropdownTrigger;
    evalDropdown.open = true;
    evalDropdown.detach.pipe(take(1)).subscribe(() => this.cdRef.detectChanges());
    this.evalDropdownOpen.emit();
  }

  private getData(): void {
    this.interviewTemplateService
      .getInterviewTemplatesSelectOptions(InterviewTemplateScope.Interview)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(templates => this.setState({ ...this.state, templates }));
  }

  private getStageId(placement: Placement | undefined): number | null {
    const stageId = placement && placement.stage && placement.stage.id;

    return placement && placement.offer && placement.offer.kind === 'job' && stageId
      ? stageId
      : null;
  }

  private findPlacement(
    offerId: number | null = null,
    talentPoolId: number | null = null,
  ): Placement | undefined {
    const candidate = this.candidate();
    return ((candidate && candidate.placements) || []).find((placement: Placement) =>
      offerId ? placement.offerId === offerId : placement.talentPoolId === talentPoolId,
    );
  }

  private createInterviewResult(value: Partial<InterviewResult>): void {
    const data = {
      ...value,
      kind: InterviewKind.QUICK,
    };
    const placement = this.findPlacement(data.offerId, data.talentPoolId);

    data.stageId = <number>this.getStageId(placement);
    this.store.dispatch(
      InterviewResultsActions.createOnePessimistic({
        payload: {
          data: {
            value: data,
            candidateId: this.candidate().id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private createInterviewRequest(value: {
    talentPoolId?: number;
    offerId?: number;
    templateId: number;
    admins: User[];
    shareOnTheGoRequestPayload?: ShareOnTheGoRequestPayload;
  }): void {
    const placement = this.findPlacement(value.offerId, value.talentPoolId);
    const stageId = this.getStageId(placement);
    const formBody = {
      interviewTemplateId: value.templateId,
      talentPoolId: value.talentPoolId,
      offerId: value.offerId,
      stageId: stageId,
    };

    const interviewResultRequestsData = value.admins.map(admin => ({
      ...formBody,
      adminId: admin.id,
    }));
    this.store.dispatch(
      InterviewResultRequestActions.createOnePessimistic({
        payload: {
          candidateId: this.candidate().id,
          data: interviewResultRequestsData,
          shareOnTheGoRequestPayload: value.shareOnTheGoRequestPayload,
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private onAddInterviewRequest(value: {
    talentPoolId?: number;
    offerId?: number;
    templateId: number;
    admins: User[];
  }) {
    this.shareOnTheGoService
      .openShareOnTheGoDialogIfPossible({
        shareOnTheGoDialogData: {
          talentPoolId: value.talentPoolId,
          offerId: value.offerId,
          candidateId: this.candidate().id,
          usersToShareWith: value.admins,
          disableSubmitOnSkipAll: true,
        },
        viewContainerRef: this.evalDropdownTpl.viewContainerRef,
      })
      .pipe(
        filterBoolean,
        switchMap((shareOnTheGoResult: ShareOnTheGoDialogResult) =>
          this.shareOnTheGoService.resultToRequestPayload({
            shareOnTheGoResult,
            offerId: value.offerId,
            talentPoolId: value.talentPoolId,
            candidateId: this.candidate().id,
          }),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(shareOnTheGoPayload => {
        this.evalDropdownTpl.toggleOverlay(false);
        this.evalDropdownClose.emit();
        this.createInterviewRequest({
          ...value,
          shareOnTheGoRequestPayload: shareOnTheGoPayload,
          admins: this.filterUsersToRequestEvalFrom({
            users: value.admins,
            offerId: value.offerId,
            talentPoolId: value.talentPoolId,
            shareOnTheGoPayload,
          }),
        });
      });
  }

  private filterUsersToRequestEvalFrom({
    users,
    shareOnTheGoPayload,
    offerId,
    talentPoolId,
  }: {
    users: User[];
    shareOnTheGoPayload: ShareOnTheGoRequestPayload;
    offerId?: number;
    talentPoolId?: number;
  }): User[] {
    return users.filter(
      requestEvalUser =>
        shareOnTheGoPayload.grantAccess.find(
          grantAccessEntry => grantAccessEntry.adminId === requestEvalUser.id,
        ) ||
        requestEvalUser.roleAbilities.includes(Ability.ViewAllJobs) ||
        (offerId && requestEvalUser.offerIds?.includes(offerId)) ||
        (talentPoolId && requestEvalUser.talentPoolIds?.includes(talentPoolId)) ||
        requestEvalUser.accessiblePlacementIds?.includes(
          this.findPlacement(offerId, talentPoolId)?.id ?? -1,
        ),
    );
  }
}
